import type { PlanDetailsCopy } from '../planDetails'

const pt: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Sua idade',
    retirement_age: 'Quando você se aposenta',
    annual_income: 'Renda anual',
    annual_savings: 'Poupança anual',
    inflation: 'Inflação',
    realization_tax_rate: 'Alíquota sobre ganho de capital',
    cost_basis: 'Custo de aquisição',
    w0: 'Patrimônio investível hoje',
    primary_residence: 'Sua residência',
    total_investable: 'Patrimônio investível',
    expected_return: 'Retorno esperado',
    volatility: 'Volatilidade',
    horizon: 'Horizonte de planejamento',
    jurisdiction: 'Residência fiscal',
    risk_aversion: 'Tolerância a risco',
    birth_year: 'Ano de nascimento',
    birth_date: 'Data de nascimento',
    marital_status: 'Estado civil',
    dependents: 'Dependentes',
    dependent_ages: 'Idades dos dependentes',
    children: 'Filhos',
    children_birth_years: 'Anos de nascimento dos filhos',
    partner_age: 'Idade do cônjuge',
    partner_annual_income: 'Renda anual do cônjuge',
    partner_retirement_age: 'Idade de aposentadoria do cônjuge',
    plan_to_age: 'Idade até a qual o plano vai',
    gross_annual_income: 'Renda bruta anual',
    net_income: 'Renda líquida',
    annual_spending: 'Gasto anual',
    essential_spending: 'Gastos essenciais',
    flexible_spending: 'Gastos flexíveis',
    investments_total: 'Total investido',
    total_investable_wealth: 'Patrimônio investível total',
    portfolio_mix: 'Composição da carteira',
    cgt_rate: 'Alíquota sobre ganhos de capital',
    interest_rate: 'Taxa de juros',
    dividend_yield: 'Rendimento de dividendos',
    wealth_tax_rate: 'Alíquota do imposto sobre o patrimônio',
    filing_jurisdictions: 'Países onde você declara',
    citizenship: 'Nacionalidade',
    tax_residency: 'Residência fiscal declarada',
    state: 'Estado ou província',
    region: 'Região',
    city: 'Cidade',
    locality: 'Município',
    church_tax_applicable: 'Sujeito ao imposto eclesiástico',
    life_cover: 'Seguro de vida',
    income_protection: 'Seguro de proteção de renda',
    employer_match: 'Contrapartida do empregador',
    pension: 'Previdência',
    state_pension: 'Aposentadoria pública',
    social_security: 'Previdência social',
    wage_growth: 'Crescimento salarial',
    stability: 'Estabilidade da renda',
    has_will: 'Testamento feito',
    risk_attitude: 'Atitude diante do risco',
  },
  keyLabels: {
    goal_priority: 'Ordem dos objetivos',
    current_age: 'Idade',
    inflation: 'Premissa de inflação',
    jurisdiction: 'Residência fiscal',
    realization_tax_rate: 'Imposto ao vender para financiar um objetivo',
    citizenship: 'Nacionalidade',
    total_investable: 'Patrimônio investível',
    annual_income: 'Renda anual',
    annual_savings: 'Poupança anual',
    risk_tier: 'Nível de risco',
    retirement_age: 'Idade de aposentadoria',
    target_wealth: 'Patrimônio-alvo',
    primary_residence_sellable: 'Venda do imóvel / mudança para um menor',
  },
  channelLabels: {
    web_levers: 'Alavancas no site',
    web_cockpit: 'Painel de decisões',
    web: 'Site',
    chat: 'Assistente de IA',
    mcp: 'Assistente de IA',
    full_review: 'Revisão completa',
    full_plan: 'Plano completo',
    what_if: 'Simulação de cenários',
  },
  objectiveLabels: {
    status_quo: 'Situação atual',
    max_goal_probability: 'Maior probabilidade de sucesso',
    lowest_drawdown: 'Menor queda máxima',
    highest_growth: 'Maior crescimento',
    fastest_to_goal: 'Objetivo atingido mais cedo',
  },
  choices: {
    interest_only: 'Somente juros',
    fixed_rate_amortizing: 'Amortização (taxa fixa)',
    variable_rate_amortizing: 'Amortização (taxa variável)',
    repayment: 'Amortização',
    refinance: 'Refinanciar à taxa de mercado',
    repay: 'Quitar integralmente',
  },
  values: {
    yearsShort: (n) => `${n} anos`,
    shareAbout: (pct) => `cerca de ${pct} do seu valor`,
    sharePart: 'parte do seu valor',
    aroundAge: (age) => ` por volta dos ${age} anos`,
    inAboutYears: (n) => ` em cerca de ${n} anos`,
    maySellFor: (share, when) => `Pode ser vendido para liberar ${share}${when}, somente se isso melhorar o plano.`,
    maySell: 'Pode ser vendido se isso melhorar o plano.',
    structuredFact: 'Dado estruturado',
  },
  ask: { talkThrough: 'Conversar sobre isso com a MyCIO' },
  review: {
    openInputCheckFailed: 'Não foi possível concluir a verificação de dados pendentes. Veja o status da revisão abaixo.',
    finishedLoading: 'Sua revisão terminou. Carregando os resultados…',
    draftReady: 'Sua revisão preliminar está pronta — abaixo estão a análise concluída, o que não pôde ser calculado e as perguntas que permitiriam concluí-la.',
    finishedNotLoaded: 'Sua revisão terminou, mas os resultados ainda não puderam ser carregados. Tentando de novo — o cálculo não será refeito.',
    fetchFailed: 'Sua revisão terminou, mas não conseguimos obtê-la agora. Ela está salva — tente novamente em instantes; não é preciso recalcular.',
    notComputed: (list) => `Não calculado nesta execução: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 alteração não foi salva' : `${f} alterações não foram salvas`} — veja Suas informações.`,
  },
  facts: {
    chooseOption: 'Escolha uma opção.',
    couldNotSave: 'Não foi possível salvar esse valor. Seu plano não mudou.',
    couldNotUndo: 'Não foi possível desfazer essa alteração.',
    choosePlaceholder: 'Escolher…',
    confirmedBadge: 'Confirmado — peça a alteração no chat',
    confirmedTitle: (was) => `Você já confirmou este dado como ${was}. Peça à MyCIO no chat para substituí-lo.`,
  },
  assumptionsToConfirm: { anInput: 'um dado', assumedSuffix: (v) => ` — premissa: ${v}` },
  editor: {
    enterNumber: 'Informe um número.',
    enterNumberFor: (l) => `Informe um número para ${l}.`,
    enterPercentFor: (l) => `Informe uma porcentagem para ${l}.`,
    chooseYesNoFor: (l) => `Escolha Sim ou Não para ${l}.`,
    semicolonBetween: (l) => `Use ";" entre os itens de ${l}.`,
    useNumbersFor: (l) => `Use números para ${l}.`,
    wholeNumbersFor: (l) => `Use números inteiros para ${l}, separados por ";".`,
    commaSeparated: 'separados por vírgula',
  },
  freshnessUnknown: 'Não conseguimos verificar se o seu cálculo salvo inclui as alterações mais recentes nas contas. Atualize a página e confira suas contas antes de se basear nestes números.',
  goals: { goalN: (n) => `Objetivo ${n}`, whatMoves: 'o que move isto →', earliest: (y) => `o mais cedo possível ≈ ${y}`, rank: 'Prioridade' },
  issues: {
    mainConstraint: 'A principal restrição deste plano',
    riskToReview: 'Risco a revisar',
    missingInformation: 'Informação pendente',
    whyItMatters: (t) => `Por que importa: ${t}`,
    seeEvidence: 'Ver a evidência ↓',
    noneFound: 'A análise concluída não identificou nenhuma questão relevante com as informações que este plano contém hoje.',
    notAvailable: 'A análise de questões ainda não está disponível. Uma seção vazia não significa que não existam questões.',
    discussQuestion: 'Revise as questões que este plano identificou. Explique a evidência de cada uma e pergunte se algum dado que falta sobre a minha situação poderia mudar a prioridade ou a conclusão.',
    discussLabel: 'Discutir estas questões',
  },
  policies: {
    optionTested: 'Opção analisada',
    computedEffect: (v) => `Efeito calculado: ${v}`,
    tradeoff: (v) => `Compensação: ${v}`,
    backToAction: 'Voltar à ação ↑',
    tradeoffsHeading: 'Compensações dos casos analisados',
  },
  transfer: {
    toHeirs: (m) => `+${m} para os herdeiros`,
    fundedPoints: (label, pts) => `${label} ${pts} pontos percentuais`,
    noChange: ' · sem mudança no financiamento dos seus objetivos',
  },
  tax: {
    rulesIntro: 'Só são exibidas as normas vinculadas a este cálculo. Cada item mostra os dados da fonte que foram registrados; datas ou informações de cobertura ausentes continuam indicadas como ausentes.',
    title: 'Tratamento tributário usado neste plano',
    countryBookPrimary: 'A projeção aplicou, ano a ano, a legislação tributária do país correspondente. As fontes registradas expressamente como oficiais ou primárias estão listadas abaixo.',
    countryBookNoPrimary: 'A projeção aplicou, ano a ano, a legislação tributária do país correspondente. Este resultado salvo ainda não tem links para fontes primárias; confira as decisões com impacto tributário antes de agir.',
    simplified: 'O cálculo usou um tratamento tributário simplificado. Revise as limitações abaixo antes de se basear em comparações com impacto tributário.',
    askQuestion: 'Explique quais leis tributárias e camadas nacionais foram aplicadas a este plano, quais foram aproximadas ou excluídas e quais fatos da minha situação tributária poderiam mudar o resultado. Use apenas as fontes primárias realmente vinculadas a este cálculo e diga quais estão faltando.',
    askLabel: 'Perguntar sobre o tratamento tributário',
  },
  inputs: { title: 'Dados que podem mudar o resultado', infoToConfirm: 'Informação a confirmar' },
  gated: {
    countChoices: (n, f) => (n === 1 ? `${f} opção analisada para você` : `${f} opções analisadas para você`),
    mappedYears: (_n, f) => `projetamos os próximos ${f} anos`,
    extraChecks: (n, f) => (n === 1 ? `${f} verificação adicional do seu plano` : `${f} verificações adicionais do seu plano`),
    placeholderRows: ['Linha ilustrativa', 'Número de exemplo', 'Opção de exemplo', 'Marcador'],
  },
}

export default pt
